import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import TextureOverlay from '../components/team/TextureOverlay';
import SiteHeader from '../components/team/SiteHeader';
import FarmGallery from '../components/team/FarmGallery';
import SectionDivider from '../components/team/SectionDivider';
import Footer from '../components/team/Footer';
import { teamMembers } from '../components/team/TeamAndContact';

const TITLE = 'About Mubarak Farm | Robusta Farm in Nabutaka, Uganda';
const DESCRIPTION = 'Mubarak Farm has grown Robusta in Nabutaka, Luweero District, Uganda since 2018. See the farm, the harvest and the people who ship it to Karachi.';
const OG_IMAGE = 'https://mubarakfarmug.com/flow-assets/content/og-about.jpg';
const TREE = '/flow-assets/content/farm/robusta-tree';

const facts = [
  { term: 'Established', detail: '2018' },
  { term: 'Location', detail: 'Nabutaka, Luweero District, Uganda' },
  { term: 'Crop', detail: 'Robusta, graded SC-12, SC-15 and SC-18' },
  { term: 'Process', detail: 'Natural, sun dried' },
  { term: 'Sold in', detail: 'Karachi, Pakistan, duty paid' },
];

const meta = {
  title: TITLE,
  meta: [
    { name: 'description', content: DESCRIPTION },
    { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://mubarakfarmug.com/about' },
    { property: 'og:image', content: OG_IMAGE },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/jpeg' },
    { property: 'og:image:alt', content: 'Coffee cherries drying in the sun at Mubarak Farm' },
    { property: 'og:site_name', content: 'Mubarak Farm Uganda' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { name: 'twitter:image', content: OG_IMAGE },
  ],
  link: [
    { rel: 'canonical', href: 'https://mubarakfarmug.com/about' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        '@id': 'https://mubarakfarmug.com/about#webpage',
        'url': 'https://mubarakfarmug.com/about',
        'name': 'About Mubarak Farm',
        'description': DESCRIPTION,
        'inLanguage': 'en',
        'isPartOf': { '@type': 'WebSite', '@id': 'https://mubarakfarmug.com/#website', 'name': 'Mubarak Farm', 'url': 'https://mubarakfarmug.com/' },
        'about': { '@type': 'Organization', '@id': 'https://mubarakfarmug.com/#organization', 'name': 'Mubarak Farm', 'url': 'https://mubarakfarmug.com/' }
      })
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://mubarakfarmug.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': 'About', 'item': 'https://mubarakfarmug.com/about' }
        ]
      })
    }
  ]
};

export default function About() {
  return (
    <React.Fragment>
      <HelmetProvider>
        <Helmet {...meta}></Helmet>
      </HelmetProvider>
      <TextureOverlay fixed />
      <SiteHeader current="/about" />
      <main>
        <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-28">
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 lg:items-end">
              <div className="lg:col-span-7">
                <nav aria-label="Breadcrumb" className="mb-8">
                  <ol className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500">
                    <li><a href="/" className="hover:text-clay transition-colors">Home</a></li>
                    <li aria-hidden="true">/</li>
                    <li aria-current="page" className="text-gray-900 font-semibold">About</li>
                  </ol>
                </nav>

                <h1 className="font-heading text-5xl sm:text-6xl leading-[1.02] tracking-tight text-gray-900 mb-6">
                  A Robusta farm in Nabutaka, Uganda.
                </h1>
                <p className="text-lg text-gray-700 max-w-xl leading-relaxed mb-10">
                  Mubarak Farm has grown Robusta in Luweero District since 2018. We grow it, dry it, hull it and ship it to Karachi ourselves, so every bag comes from one farm.
                </p>

                <dl className="divide-y divide-gray-300/70 border-y border-gray-300/70">
                  {facts.map((fact) => (
                    <div key={fact.term} className="grid grid-cols-12 gap-4 py-4">
                      <dt className="col-span-4 sm:col-span-3 text-sm text-gray-600 pt-0.5">{fact.term}</dt>
                      <dd className="col-span-8 sm:col-span-9 text-lg text-gray-900 leading-snug">{fact.detail}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <figure className="lg:col-span-5">
                <img
                  className="w-full aspect-[4/3] lg:aspect-[3/4] object-cover rounded-2xl"
                  src={`${TREE}-800.webp`}
                  srcSet={`${TREE}-800.webp 800w, ${TREE}-1400.webp 1400w`}
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  width="1400"
                  height="1867"
                  alt="A Robusta tree on the farm with clusters of green and ripening red cherries along its branches"
                  fetchpriority="high"
                />
                <figcaption className="mt-3 text-sm text-gray-600">A Robusta tree on the farm, September 2026.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <SectionDivider />

        <section className="relative py-20 lg:py-28">
          <FarmGallery headingAs="h2" />
        </section>

        <SectionDivider />

        <section className="relative py-20 lg:py-28">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight text-gray-900 mb-6">
                  The team
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The team works across Uganda, Pakistan and the United Kingdom. The farm itself is run from Nabutaka.
                </p>
              </div>
              <ul className="lg:col-span-8 divide-y divide-gray-300/70 border-y border-gray-300/70">
                {teamMembers.map((member) => (
                  <li key={member.name} className="grid grid-cols-1 sm:grid-cols-[14rem_1fr_auto] gap-1 sm:gap-6 py-5 sm:items-baseline">
                    <p className="font-heading text-2xl text-gray-900">{member.name}</p>
                    <p className="text-gray-700">{member.role}</p>
                    <p className="sm:text-right text-sm text-gray-500">{member.location}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="visit" className="relative pb-20 lg:pb-28">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 md:items-center">
              <figure className="md:col-span-5">
                <img
                  className="w-full aspect-[16/9] object-cover rounded-2xl"
                  src="/flow-assets/content/visitor-area.jpg"
                  width="512"
                  height="288"
                  alt="Thatched-roof gazebo with a Visitors Area sign on the farm"
                  loading="lazy"
                />
              </figure>
              <div className="md:col-span-7">
                <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight text-gray-900 mb-6">
                  Visit the farm
                </h2>
                <p className="text-lg text-gray-700 max-w-xl leading-relaxed mb-8">
                  Tours are by appointment. Send a message with the dates you have in mind and we will arrange it with the team in Nabutaka.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-400 text-gray-900 font-bold px-8 py-4 rounded-full transition-[background-color,transform] duration-200 active:scale-[0.97]"
                  >
                    Arrange a visit
                  </a>
                  <a href="/wholesale" className="font-semibold text-clay hover:text-gray-900 underline underline-offset-4 transition-colors">
                    See wholesale prices
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </React.Fragment>
  );
}
